/**
 * AR Camera Controller
 * Combines device orientation and motion data for smooth AR camera control
 */

export class ARCameraController {
  // Smoothing factors
  private readonly ORIENTATION_SMOOTHING = 0.8
  
  // Current smoothed values
  private smoothedOrientation = { alpha: 0, beta: 0, gamma: 0 }
  private smoothedPosition = { x: 0, y: 0, z: 0 }
  
  // Velocity tracking for position
  private velocity = { x: 0, y: 0, z: 0 }
  private lastUpdateTime = Date.now()
  
  // Calibration
  private calibrationOffset = { alpha: 0, beta: 0, gamma: 0 }
  private isCalibrated = false
  
  // World space tracking
  private worldMode = false
  private initialCameraPosition = { x: 0, y: 1.6, z: 0 } // Eye height
  private worldCameraPosition = { x: 0, y: 1.6, z: 0 }
  private enableMovementTracking = false
  private movementScale = 0.05 // Scale factor for movement sensitivity
  private defaultCameraOffset = { x: 0, y: 0, z: 0 } // Offset from defaultCamera
  
  /**
   * Update camera from device orientation
   */
  updateOrientation(orientation: { alpha: number; beta: number; gamma: number }) {
    // Apply smoothing
    this.smoothedOrientation.alpha = this.lerp(
      this.smoothedOrientation.alpha,
      orientation.alpha - this.calibrationOffset.alpha,
      this.ORIENTATION_SMOOTHING
    )
    this.smoothedOrientation.beta = this.lerp(
      this.smoothedOrientation.beta,
      orientation.beta - this.calibrationOffset.beta,
      this.ORIENTATION_SMOOTHING
    )
    this.smoothedOrientation.gamma = this.lerp(
      this.smoothedOrientation.gamma,
      orientation.gamma - this.calibrationOffset.gamma,
      this.ORIENTATION_SMOOTHING
    )
    
    // Auto-calibrate on first update
    if (!this.isCalibrated) {
      this.calibrate()
    }
  }
  
  /**
   * Update camera from device motion
   */
  updateMotion(motion: {
    acceleration: { x: number; y: number; z: number }
    rotationRate: { alpha: number; beta: number; gamma: number }
  }) {
    const now = Date.now()
    const deltaTime = (now - this.lastUpdateTime) / 1000 // Convert to seconds
    this.lastUpdateTime = now
    
    // Filter out gravity (approximately 9.8 m/s² on y-axis when device is upright)
    const gravityThreshold = 8.0
    const filteredAccel = {
      x: motion.acceleration.x,
      y: Math.abs(motion.acceleration.y) > gravityThreshold ? 0 : motion.acceleration.y,
      z: motion.acceleration.z
    }
    
    // Update velocity from acceleration (integrate)
    this.velocity.x += filteredAccel.x * deltaTime
    this.velocity.y += filteredAccel.y * deltaTime
    this.velocity.z += filteredAccel.z * deltaTime
    
    // Apply stronger damping for stability
    const damping = this.enableMovementTracking ? 0.9 : 0.95
    this.velocity.x *= damping
    this.velocity.y *= damping
    this.velocity.z *= damping
    
    // Update position from velocity (integrate)
    this.smoothedPosition.x += this.velocity.x * deltaTime * this.movementScale
    this.smoothedPosition.y += this.velocity.y * deltaTime * this.movementScale
    this.smoothedPosition.z += this.velocity.z * deltaTime * this.movementScale
    
    // Update world camera position if movement tracking is enabled
    if (this.worldMode && this.enableMovementTracking) {
      // Apply movement relative to initial position + defaultCamera offset
      this.worldCameraPosition.x = this.initialCameraPosition.x + this.defaultCameraOffset.x + this.smoothedPosition.x
      this.worldCameraPosition.y = this.initialCameraPosition.y + this.defaultCameraOffset.y + this.smoothedPosition.y
      this.worldCameraPosition.z = this.initialCameraPosition.z + this.defaultCameraOffset.z + this.smoothedPosition.z
    }
  }
  
  /**
   * Get camera transform for rendering
   */
  getCameraTransform(screenOrientation: number = 0) {
    // Convert degrees to radians
    const alpha = this.smoothedOrientation.alpha * Math.PI / 180
    const beta = this.smoothedOrientation.beta * Math.PI / 180
    const gamma = this.smoothedOrientation.gamma * Math.PI / 180
    
    // Adjust for screen orientation
    let rx, ry, rz
    
    if (Math.abs(screenOrientation) === 90) {
      // Landscape mode
      rx = -gamma
      ry = alpha
      rz = beta
    } else {
      // Portrait mode - adjust for vertical phone position
      rx = -(beta - Math.PI / 2)
      ry = alpha
      rz = gamma
    }
    
    // In world mode, camera moves in world space
    if (this.worldMode) {
      // Calculate camera position based on orientation
      // This simulates moving the camera, not the world
      const forward = {
        x: Math.sin(ry) * Math.cos(rx),
        y: Math.sin(rx),
        z: Math.cos(ry) * Math.cos(rx)
      }
      
      return {
        position: this.worldCameraPosition,
        rotation: {
          x: rx,
          y: ry,
          z: rz
        },
        lookDirection: forward
      }
    }
    
    // Legacy mode - model follows camera
    return {
      position: {
        x: this.smoothedPosition.x * 0.1, // Scale down motion
        y: this.smoothedPosition.y * 0.1,
        z: this.smoothedPosition.z * 0.1
      },
      rotation: {
        x: rx,
        y: ry,
        z: rz
      }
    }
  }
  
  /**
   * Calibrate the controller (set current orientation as zero)
   */
  calibrate() {
    this.calibrationOffset = {
      alpha: this.smoothedOrientation.alpha,
      beta: this.smoothedOrientation.beta,
      gamma: this.smoothedOrientation.gamma
    }
    this.isCalibrated = true
    
    // Reset position
    this.smoothedPosition = { x: 0, y: 0, z: 0 }
    this.velocity = { x: 0, y: 0, z: 0 }
  }
  
  /**
   * Reset the controller
   */
  reset() {
    this.smoothedOrientation = { alpha: 0, beta: 0, gamma: 0 }
    this.smoothedPosition = { x: 0, y: 0, z: 0 }
    this.velocity = { x: 0, y: 0, z: 0 }
    this.calibrationOffset = { alpha: 0, beta: 0, gamma: 0 }
    this.isCalibrated = false
  }
  
  /**
   * Enable world mode for AR
   */
  setWorldMode(enabled: boolean, defaultCamera?: { position: { x: number; y: number; z: number } }) {
    this.worldMode = enabled
    if (enabled) {
      // If defaultCamera provided, use it as baseline
      if (defaultCamera?.position) {
        this.defaultCameraOffset = {
          x: defaultCamera.position.x - this.initialCameraPosition.x,
          y: defaultCamera.position.y - this.initialCameraPosition.y,
          z: defaultCamera.position.z - this.initialCameraPosition.z
        }
        this.worldCameraPosition = { ...defaultCamera.position }
      } else {
        // Reset to initial position
        this.defaultCameraOffset = { x: 0, y: 0, z: 0 }
        this.worldCameraPosition = { ...this.initialCameraPosition }
      }
      
      // Reset motion tracking
      this.smoothedPosition = { x: 0, y: 0, z: 0 }
      this.velocity = { x: 0, y: 0, z: 0 }
    }
  }
  
  /**
   * Get world mode status
   */
  isWorldMode() {
    return this.worldMode
  }
  
  /**
   * Enable or disable movement tracking
   */
  setMovementTracking(enabled: boolean) {
    this.enableMovementTracking = enabled
    if (!enabled) {
      // Reset velocity when disabling
      this.velocity = { x: 0, y: 0, z: 0 }
    }
  }
  
  /**
   * Set movement sensitivity scale
   */
  setMovementScale(scale: number) {
    this.movementScale = Math.max(0.01, Math.min(1.0, scale))
  }
  
  /**
   * Get current movement tracking status
   */
  isMovementTrackingEnabled() {
    return this.enableMovementTracking
  }
  
  /**
   * Linear interpolation helper
   */
  private lerp(start: number, end: number, factor: number): number {
    return start + (end - start) * factor
  }
}